/**
 * `ResizableSplit` — two columns with a draggable divider between them.
 *
 * ## Why it moved here
 *
 * It was `pages/main/StudioPage/components/ResizableSplit.tsx`, i.e. a layer-3 page
 * component. The Analytics "Scans by location" card needs the same thing (list | map),
 * so §36's rule fires on the SECOND hand-roll: the answer is one shared component, not
 * a second resizer with its own drag maths. §47's direction is likewise down, not
 * sideways — the improvements below (px bounds, keyboard, a flush variant, a drag-end
 * commit signal) live in the shared component so Studio gets them too.
 *
 * Studio's behaviour is unchanged: `unit` defaults to `'pct'` and the gutter variant is
 * the default, which is exactly what it rendered before.
 *
 * ## Two variants, because a divider is not always a gutter
 *
 * | variant | divider | for |
 * |---|---|---|
 * | `gutter` (default) | an 18px lane with a centred pill | panes separated by whitespace (Studio's editor ↔ preview) |
 * | `flush` | a 1px rule + a centred 3x32 grip, hit area widened by an overlay | panes that share an edge inside one bordered, clipped container (the map card) |
 *
 * `flush` matters because the map card draws ONE border around both panes and clips to
 * a radius. An 18px whitespace lane inside that container would read as a gap sawn out
 * of a single surface. The 1px rule keeps the layout width at 1px and widens only the
 * POINTER target, via an absolutely-positioned overlay that overhangs both panes — so
 * the divider is grabbable at ~9px without costing 9px of map.
 *
 * Its grip is the Ask panel's, deliberately: `packages`-adjacent chrome should not invent
 * a second "drag me" language. Same 3px x 32px pill at `borderRadius: 999`, same quiet
 * rest state (`divider` at 60% opacity), same full `brand[600]` on hover, focus and drag,
 * same 120ms colour/opacity transition — see `components/navigation/AskPanel`. The one
 * divergence is documented at the call site: the pill is centred rather than nudged 2px,
 * because this divider belongs to neither pane.
 *
 * ## Units: percent OR pixels, and the choice is not cosmetic
 *
 * Studio's split is a reading-width preference between two flexible columns, so percent
 * is right — it holds its proportion at any window size. The map card's list column is a
 * CONTENT column whose minimum is set by what has to fit in a row (a 44px thumbnail, a
 * title, a status chip, a relative time). A percentage there means the same setting is
 * a comfortable 400px in one grid position and an unreadable 240px in another, because
 * the card's own width changes with its column span and with the Components panel. So
 * `unit: 'px'` exists, and the bounds are px too.
 *
 * ## Keyboard is not optional
 *
 * A pointer-only separator is unreachable for anyone not using a mouse, and a resizable
 * split has a well-defined ARIA contract for exactly this: `role="separator"` with
 * `aria-valuenow` / `min` / `max` is a focusable, arrow-operable widget. Arrow keys step,
 * Home/End jump to the bounds, and every keyboard adjustment fires `onDragEnd` because a
 * keypress IS a committed change — there is no release to wait for. That signal is what
 * lets a consumer defer expensive work (the map card remounts Google Maps on it) to the
 * end of a gesture instead of per frame.
 */
import React, { useCallback, useRef, useState } from 'react'

import { Box, useTheme } from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import { zoomOf } from 'utils/textZoom'

/**
 * Half-width of the `flush` variant's invisible grab strip, in px.
 *
 * The strip overhangs the 1px rule by this much on each side, so the pointer target is
 * `1 + 2 * 4 = 9px` — at or above the 8px the Ask panel settled on (`HANDLE_WIDTH`),
 * whose own comment records that narrower than that is hard to hit. It costs the panes
 * nothing because the strip is absolutely positioned.
 */
const FLUSH_HIT_OVERHANG = 4

export type ResizableSplitUnit = 'pct' | 'px'
export type ResizableSplitVariant = 'gutter' | 'flush'

export interface ResizableSplitProps {
  left: React.ReactNode
  right: React.ReactNode
  /** Left-column size, in `unit`s. Controlled. */
  leftSize: number
  /** Fired continuously during a drag, so the panes track the pointer. */
  onLeftSizeChange: (size: number) => void
  /**
   * Fired ONCE at the end of a gesture — pointer release, or each keyboard step.
   *
   * Consumers that must do something expensive on a settled size (remount a map,
   * persist a preference) hang it here rather than debouncing `onLeftSizeChange`
   * themselves.
   */
  onDragEnd?: (size: number) => void
  unit?: ResizableSplitUnit
  min?: number
  max?: number
  variant?: ResizableSplitVariant
  /** Hides the divider and freezes the size. Used when the left pane is collapsed. */
  disabled?: boolean
  /** Accessible name for the separator. */
  label?: string
  /** Arrow-key increment, in `unit`s. Defaults to 2 (pct) / 24 (px). */
  step?: number
  sx?: SxProps<Theme>
  /**
   * Animate the left pane's width with a CSS transition — for a CONSUMER toggling
   * `leftSize` between two discrete values (e.g. a collapsed rail vs. its expanded
   * width), so the change reads as a smooth open/close rather than a snap.
   *
   * Automatically suppressed while a gesture is genuinely live — a pointer drag
   * (`isDragging`) or a held keyboard step (`keySizeRef`), both already tracked by this
   * component for its own grip styling / commit timing. Animating on top of either would
   * make the pane visibly lag the input instead of tracking it 1:1, which is the one
   * thing a resize gesture cannot tolerate. Off by default — Studio's split is unaffected.
   */
  animateWidth?: boolean
  /**
   * Fires once the left pane's OWN CSS width transition finishes — i.e. only when
   * `animateWidth` was active for that particular change. The one moment a consumer can
   * trust the pane has actually REACHED its new width, as opposed to
   * `onDragEnd`/`onLeftSizeChange`, which report the target value immediately, before any
   * animation has had a chance to play out.
   */
  onLeftWidthTransitionEnd?: () => void
}

export const ResizableSplit = ({
  left,
  right,
  leftSize,
  onLeftSizeChange,
  onDragEnd,
  unit = 'pct',
  min = unit === 'pct' ? 30 : 200,
  max = unit === 'pct' ? 75 : 640,
  variant = 'gutter',
  disabled = false,
  label = 'Resize panels',
  step = unit === 'pct' ? 2 : 24,
  sx,
  animateWidth = false,
  onLeftWidthTransitionEnd,
}: ResizableSplitProps) => {
  const theme = useTheme()
  const containerRef = useRef<HTMLDivElement>(null)

  const clamp = useCallback((n: number) => Math.max(min, Math.min(max, n)), [min, max])

  const sizeAt = useCallback(
    (clientX: number): number | null => {
      const rect = containerRef.current?.getBoundingClientRect()
      if (!rect || rect.width === 0) return null
      const px = clientX - rect.left
      // `pct` is a ratio of two painted lengths, so the text zoom cancels out. `px` is
      // written back as a LAYOUT width inside `#app`'s zoom, so the painted distance has
      // to be divided by it first — otherwise the divider lands at z × the pointer's
      // offset (at 0.8 it trailed the cursor by a fifth of the column).
      return clamp(unit === 'pct' ? (px / rect.width) * 100 : px / zoomOf(containerRef.current))
    },
    [clamp, unit],
  )

  /**
   * Whether a pointer drag is in flight — the one bit of drag state that lives in React
   * rather than in the closure above, because the `flush` grip paints itself in the brand
   * colour while dragging (same treatment as the Ask panel's grip).
   *
   * Two writes per gesture, on press and on release — NOT one per `mousemove` — so this
   * adds no re-render the controlled `onLeftSizeChange` was not already causing.
   */
  const [isDragging, setIsDragging] = useState(false)

  const startDrag = (e: React.MouseEvent) => {
    if (disabled) return
    e.preventDefault()
    let latest = leftSize
    setIsDragging(true)
    const onMove = (ev: MouseEvent) => {
      const next = sizeAt(ev.clientX)
      if (next === null) return
      latest = next
      onLeftSizeChange(next)
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      setIsDragging(false)
      onDragEnd?.(latest)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  /**
   * The size a key-driven gesture has reached, and whether one is in flight.
   *
   * A keyboard resize commits on KEY UP, not on key down, and the difference is not
   * pedantic: a held arrow key auto-repeats at ~30Hz, so committing per keydown would
   * fire `onDragEnd` thirty times a second — and this component's first consumer remounts
   * Google Maps on that signal. Key-up is the true end of the gesture, exactly as pointer
   * release is for a drag, and a held key produces exactly one of them.
   */
  const keySizeRef = useRef<number | null>(null)

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return
    // Read from the ref while a gesture is in flight: with auto-repeat, the next keydown
    // arrives before React has re-rendered with the previous value, so stepping off the
    // `leftSize` prop would stall on the same number.
    const base = keySizeRef.current ?? leftSize
    let next: number | null = null
    if (e.key === 'ArrowLeft') next = clamp(base - step)
    else if (e.key === 'ArrowRight') next = clamp(base + step)
    else if (e.key === 'Home') next = min
    else if (e.key === 'End') next = max
    if (next === null) return
    e.preventDefault()
    keySizeRef.current = next
    onLeftSizeChange(next)
  }

  const onKeyUp = (e: React.KeyboardEvent) => {
    if (keySizeRef.current === null) return
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return
    const settled = keySizeRef.current
    keySizeRef.current = null
    onDragEnd?.(settled)
  }

  // A gesture abandoned by tabbing away still has to commit, or the consumer is left
  // holding a stale settled value for a size the user can plainly see has changed.
  const onBlur = () => {
    if (keySizeRef.current === null) return
    const settled = keySizeRef.current
    keySizeRef.current = null
    onDragEnd?.(settled)
  }

  // Shared by both variants. `aria-valuetext` is what a screen reader actually announces
  // — "42 percent" / "400 pixels" — since the bare number means nothing on its own.
  const separatorA11y = {
    role: 'separator' as const,
    'aria-orientation': 'vertical' as const,
    'aria-label': label,
    'aria-valuenow': Math.round(leftSize),
    'aria-valuemin': min,
    'aria-valuemax': max,
    'aria-valuetext': unit === 'pct' ? `${Math.round(leftSize)} percent` : `${Math.round(leftSize)} pixels`,
    tabIndex: disabled ? -1 : 0,
    onMouseDown: startDrag,
    onKeyDown,
    onKeyUp,
    onBlur,
  }

  // Read directly during render: `keySizeRef` is mutated synchronously inside the SAME
  // event handlers (`onKeyDown`/`onKeyUp`) that also call `onLeftSizeChange`, which is
  // what causes the re-render this value feeds — so by the time this runs, the ref
  // already reflects the gesture state as of that same tick.
  const isGestureLive = isDragging || keySizeRef.current !== null

  // `animateWidth` opts a consumer's DISCRETE jumps (e.g. a collapse toggle) into a CSS
  // transition, but must never animate the CONTINUOUS per-pixel changes a live drag or
  // held keyboard step already produces — see the prop's own doc comment.
  const widthTransitionSx =
    animateWidth && !isGestureLive
      ? {
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            // Matches the platform's own precedent for this exact shape of interaction —
            // a panel's width snapping between two states — rather than inventing a
            // curve: `Sidebar`'s expanded/collapsed rail uses `easing.sharp` with MUI's
            // semantic duration tokens. Simplified to one duration for both directions
            // (Sidebar uses separate enter/leave mixins); the ~30ms difference between
            // `enteringScreen`/`leavingScreen` is not perceptible here and one value is
            // easier to reason about for a single togglable Box.
            duration: theme.transitions.duration.standard,
          }),
        }
      : undefined

  const handleTransitionEnd = onLeftWidthTransitionEnd
    ? (e: React.TransitionEvent<HTMLDivElement>) => {
        // `propertyName === 'width'` + `target === currentTarget`: only THIS box's own
        // width transition, not an unrelated transition on a descendant (e.g. a list row)
        // bubbling up.
        if (e.propertyName === 'width' && e.target === e.currentTarget) onLeftWidthTransitionEnd()
      }
    : undefined

  return (
    <Box
      ref={containerRef}
      // No bottom margin. The 64px that was here was clearance for the save bar pinned to the
      // viewport bottom, which VRG-221 moved into the page header — without the bar it is a
      // dead strip, and the scroll container's own 24px bottom padding is already the page
      // gutter (§22: the bottom gap matches the 24px left/right one).
      sx={{ display: 'flex', width: '100%', alignItems: 'stretch', ...(sx as object) }}
    >
      <Box
        onTransitionEnd={handleTransitionEnd}
        sx={
          unit === 'pct'
            ? { width: `${leftSize}%`, minWidth: 0, ...widthTransitionSx }
            : { width: leftSize, minWidth: 0, flexShrink: 0, ...widthTransitionSx }
        }
      >
        {left}
      </Box>

      {!disabled &&
        (variant === 'gutter' ? (
          <Box
            {...separatorA11y}
            sx={{
              width: 18,
              flexShrink: 0,
              cursor: 'col-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover .vera-split-handle, &:active .vera-split-handle, &:focus-visible .vera-split-handle':
                { backgroundColor: 'primary.main', height: 64 },
            }}
          >
            <Box
              className="vera-split-handle"
              sx={{
                width: 4,
                height: 48,
                borderRadius: 2,
                backgroundColor: 'grey.300',
                transition: 'background-color .15s ease, height .15s ease',
              }}
            />
          </Box>
        ) : (
          <Box
            {...separatorA11y}
            sx={{
              position: 'relative',
              // ONE pixel of layout. The grab target is the overlay child below, which is
              // absolutely positioned and therefore costs the panes nothing.
              width: '1px',
              flexShrink: 0,
              alignSelf: 'stretch',
              cursor: 'col-resize',
              backgroundColor: theme.palette.primary[100],
              transition: 'background-color .15s ease',
              outline: 'none',
              // The affordance lives on the GRIP, not on the full-height rule — matching
              // the Ask panel, where the seam stays quiet and the pill is what confirms
              // itself the moment you touch it. A 1px rule going brand down the whole
              // height of the card is a lot of purple for a hover.
              '&:hover .vera-split-grip, &:focus-visible .vera-split-grip': {
                backgroundColor: theme.palette.brand[600],
                opacity: 1,
              },
              // Focus has to be visible on a 1px element, and an outline on something
              // 1px wide is unreadable — so the rule itself widens and takes the brand
              // colour, which is the same "brand on focus" language every field uses (§31).
              // Kept in ADDITION to the grip recolour above: a 3x32 pill changing colour
              // is a weak focus indicator on its own, and this is the keyboard path's only
              // ring. The Ask panel needs no equivalent because its handle is not a seam.
              '&:focus-visible': { width: '3px', backgroundColor: theme.palette.brand[600] },
            }}
          >
            {/*
              The invisible grab strip. Straddles the rule so the target is symmetric
              about the seam, and sits above the panes so a press near it starts a drag
              rather than landing on a list row or the map canvas.

              `touchAction: 'none'` is what stops a touch drag from also scrolling the
              page underneath the divider.
            */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: -FLUSH_HIT_OVERHANG,
                right: -FLUSH_HIT_OVERHANG,
                zIndex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                touchAction: 'none',
              }}
            >
              {/*
                A VISIBLE grip, not just an invisible hit area — the strip around it is
                the (generous) target, this pill is what says "drag me". Quiet at rest
                (divider at 60%), full brand on hover, focus and drag.

                `pointerEvents: 'none'` keeps it out of the hit test, so the separator
                above stays the event target for every handler in `separatorA11y`.

                Unlike the Ask panel's grip this is NOT nudged off centre. There the pill
                sits on the panel's own border and the 2px offset makes it read as the
                panel's affordance rather than straddling the seam; here the divider
                belongs to neither pane, so dead centre is the honest position.
              */}
              <Box
                className="vera-split-grip"
                sx={{
                  width: 3,
                  height: 32,
                  borderRadius: 999,
                  pointerEvents: 'none',
                  backgroundColor: isDragging ? theme.palette.brand[600] : theme.palette.divider,
                  opacity: isDragging ? 1 : 0.6,
                  transition: 'background-color 120ms ease, opacity 120ms ease',
                }}
              />
            </Box>
          </Box>
        ))}

      <Box sx={{ flex: 1, minWidth: 0 }}>{right}</Box>
    </Box>
  )
}
